import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { PlayerMovement } from './PlayerMovement.js';
import { PlayerCollision } from './PlayerCollision.js';
import { PlayerCameraRig } from './PlayerCameraRig.js';
import { FootstepController } from './FootstepController.js';
import { PointerLockInput } from './PointerLockInput.js';

/**
 * First-person player: input, movement, collision, camera rig, footsteps.
 */
export class Player {
  /**
   * @param {{
   *   canvas: HTMLCanvasElement;
   *   input: import('../game/InputManager.js').InputManager;
   *   camera: import('../rendering/Camera.js').CameraSystem;
   *   world: import('../world/World.js').World;
   *   config?: typeof CONFIG.player;
   * }} deps
   */
  constructor({ canvas, input, camera, world, config = CONFIG.player }) {
    this.input = input;
    this.camera = camera;
    this.world = world;
    this.config = config;

    this.position = new THREE.Vector3(
      config.spawn.x,
      config.spawn.y,
      config.spawn.z,
    );
    this.velocity = new THREE.Vector3();
    this.onGround = false;

    this.look = new PointerLockInput({ canvas });
    this.look.onLockChange = (locked) => {
      if (this._onLockChange) this._onLockChange(locked);
    };
    this.look.onLookSync = (yaw, pitch) => {
      this.cameraRig.syncLook(yaw, pitch);
    };

    this.movement = new PlayerMovement(config);
    this.collision = new PlayerCollision({
      radius: config.radius,
      height: config.bodyHeight,
      groundSnapDistance: config.groundSnapDistance,
    });
    this.cameraRig = new PlayerCameraRig({ camera, config });
    this.footsteps = new FootstepController({
      minSpeed: config.footstep.minSpeed,
      onFootstep: (payload) => this._emitFootstep(payload),
    });

    /** @type {((payload: import('./FootstepController.js').FootstepEvent) => void)[]} */
    this._footstepListeners = [];
    /** @type {((locked: boolean) => void) | null} */
    this._onLockChange = null;
    /** @type {boolean} */
    this._interactionEnabled = true;
    /** @type {boolean} */
    this._movementEnabled = true;
  }

  /** @param {(payload: import('./FootstepController.js').FootstepEvent) => void} listener */
  addFootstepListener(listener) {
    this._footstepListeners.push(listener);
  }

  /** @param {(payload: import('./FootstepController.js').FootstepEvent) => void} listener */
  removeFootstepListener(listener) {
    this._footstepListeners = this._footstepListeners.filter((l) => l !== listener);
  }

  /** Call after world colliders exist. */
  initializeSpawn(spawn = this.config.spawn) {
    this.position.set(spawn.x, spawn.y, spawn.z);
    this.velocity.set(0, 0, 0);
    this.position.y = this.collision.findGroundHeight(
      this.position,
      this.world.getColliders(this.position),
    );
    this.onGround = true;
    this.cameraRig.syncLook(this.look.yaw, this.look.pitch);
    this.cameraRig.apply({
      feet: this.position,
      horizontalSpeed: 0,
      onGround: true,
      delta: 0,
    });
  }

  /** @param {(locked: boolean) => void} handler */
  setLockChangeHandler(handler) {
    this._onLockChange = handler;
  }

  attach() {
    if (this._interactionEnabled) {
      this.look.attach();
    }
  }

  /** @param {boolean} enabled */
  setInteractionEnabled(enabled) {
    this._interactionEnabled = enabled;
    if (enabled) {
      this.look.attach();
    } else {
      this.look.detach();
    }
  }

  /** @param {boolean} enabled */
  setMovementEnabled(enabled) {
    this._movementEnabled = enabled;
    if (!enabled) {
      this.velocity.set(0, 0, 0);
    }
  }

  update(delta) {
    if (!this.look.isLocked) {
      this.cameraRig.updateLook(this.look.yaw, this.look.pitch, delta);
      this.cameraRig.apply({
        feet: this.position,
        horizontalSpeed: 0,
        onGround: this.onGround,
        delta,
      });
      return;
    }

    const view = this.cameraRig.updateLook(
      this.look.yaw,
      this.look.pitch,
      delta,
    );

    if (!this._movementEnabled) {
      this.cameraRig.apply({
        feet: this.position,
        horizontalSpeed: 0,
        onGround: this.onGround,
        delta,
      });
      return;
    }

    const moveResult = this.movement.apply({
      input: this.input,
      lookYaw: view.displayYaw,
      lookPitch: view.displayPitch,
      onGround: this.onGround,
      velocity: this.velocity,
      delta,
    });

    const collisionResult = this.collision.move(
      this.position,
      this.velocity,
      this.world.getColliders(this.position),
      delta,
    );
    this.onGround = collisionResult.onGround;

    this.footsteps.update({
      horizontalSpeed: moveResult.horizontalSpeed,
      onGround: this.onGround,
      sprinting: moveResult.sprinting,
      delta,
    });

    this.cameraRig.apply({
      feet: this.position,
      horizontalSpeed: moveResult.horizontalSpeed,
      onGround: this.onGround,
      delta,
    });
  }

  dispose() {
    this.look.detach();
    this._footstepListeners = [];
  }

  _emitFootstep(payload) {
    for (const listener of this._footstepListeners) {
      listener(payload);
    }
  }
}
